import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydInput from '../../../../commonDesign/SydInput'
import Icon from '../../../../atoms/Icon'
import presets from './presets'

const presetOptions = [
  { label: 'Select a preset...', value: '' },
  ...Object.entries(presets).map(([key, preset]) => ({
    label: preset.displayName,
    value: key
  }))
]

function RedtailMetricConfigEditor ({ config, styleVariant, onUpdateConfig, onUpdateStyleVariant }) {
  const [local, setLocal] = useState(config || {})

  useEffect(() => {
    setLocal(config || {})
  }, [config])

  const selectedPreset = useMemo(() => {
    return local.preset ? presets[local.preset] : null
  }, [local.preset])

  const propagate = useCallback((next) => {
    setLocal(next)
    onUpdateConfig(next)
  }, [onUpdateConfig])

  const handlePresetChange = useCallback((e) => {
    const preset = e.target.value || null
    propagate({ ...local, preset })
  }, [local, propagate])

  const handleFieldChange = useCallback((field) => (e) => {
    const value = e.target.value || null
    propagate({ ...local, [field]: value })
  }, [local, propagate])

  const sizeOptions = [
    { label: 'Small', value: 'sm' },
    { label: 'Medium', value: 'md' },
    { label: 'Large', value: 'lg' }
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div>
        <SydLabel required label='Preset' helperText='Select the metric type this component displays'>
          <SydSelect
            options={presetOptions}
            value={local.preset || ''}
            onChange={handlePresetChange}
          />
        </SydLabel>
      </div>

      <div>
        <SydLabel label='Title' helperText='Override the preset default title'>
          <SydInput
            value={local.title || ''}
            onChange={handleFieldChange('title')}
            placeholder={selectedPreset?.defaults?.title || 'Title'}
          />
        </SydLabel>
      </div>

      <div>
        <SydLabel label='Label' helperText='Override the subtitle shown below the value'>
          <SydInput
            value={local.label || ''}
            onChange={handleFieldChange('label')}
            placeholder={selectedPreset?.defaults?.label || 'Label'}
          />
        </SydLabel>
      </div>

      <div>
        <SydLabel label='Icon' helperText='Icon name from the library (e.g. redtailSmall, redtail)'>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ flex: 1 }}>
              <SydInput
                value={local.icon || ''}
                onChange={handleFieldChange('icon')}
                placeholder={selectedPreset?.defaults?.icon || 'Icon name'}
              />
            </div>
            {(local.icon || selectedPreset?.defaults?.icon) && (
              <div style={{
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid var(--color-neutral-300)',
                borderRadius: '4px',
                flexShrink: 0
              }}
              >
                <Icon
                  name={local.icon || selectedPreset?.defaults?.icon}
                  customSize='20px'
                />
              </div>
            )}
          </div>
        </SydLabel>
      </div>

      <div>
        <SydLabel label='Format' helperText='numeral.js format string (e.g. 0,0 or 0.00)'>
          <SydInput
            value={local.format || ''}
            onChange={handleFieldChange('format')}
            placeholder={selectedPreset?.defaults?.format || '0,0'}
          />
        </SydLabel>
      </div>

      <div>
        <SydLabel label='Size' helperText='Card size variant'>
          <SydSelect
            options={sizeOptions}
            value={styleVariant || 'md'}
            onChange={(e) => onUpdateStyleVariant(e.target.value)}
          />
        </SydLabel>
      </div>
    </div>
  )
}

RedtailMetricConfigEditor.propTypes = {
  config: PropTypes.object,
  styleVariant: PropTypes.string,
  onUpdateConfig: PropTypes.func.isRequired,
  onUpdateStyleVariant: PropTypes.func.isRequired
}

export default RedtailMetricConfigEditor
